/**
 * Mock data + live-state engine for the Stats dashboard's **Top Games** board.
 *
 * The rest of the Stats page is wired to the Owners Hub Stats API: the Brand metrics
 * + Operational Performance via `pages/stats/useStatsMetrics.ts`, and the Spotlight's
 * **Top Players** board via `pages/stats/useTopPlayers.ts` (`daily_player_activity`).
 * Only **Top Games** remains mocked, because no game-level data set backs it yet
 * (the stats API exposes no `game` dimension). This file owns that one live-state
 * slice (`G4`), its seed + next-tick jitter and the `selectSpotlight` view selector.
 * When a games endpoint lands, replace the selector with one yielding the same
 * `StatsTopTable` shape and delete this file.
 */
import type { StatsTopTable } from './types';

// --- Live-state slice (Top Games only) ---------------------------------------
export type StatsLiveG4 = {
  topGamesStakes: number[];
};

// --- Update interval (ms) ----------------------------------------------------
export const STATS_GROUP_INTERVALS = {
  group4: 4000,
} as const;

export const initialStatsG4: StatsLiveG4 = {
  topGamesStakes: [2000, 1800, 1700, 1300, 1100],
};

// --- Jitter helpers ----------------------------------------------------------
function fluctuate(value: number, maxPct: number, decimals = 0): number {
  const delta = value * maxPct * (Math.random() * 2 - 1);
  return parseFloat((value + delta).toFixed(decimals));
}

function fluctuateArray(arr: number[], maxPct: number): number[] {
  return arr.map((v) => Math.max(0, Math.round(fluctuate(v, maxPct))));
}

// --- Next-tick generator -----------------------------------------------------
export function nextStatsG4(prev: StatsLiveG4): StatsLiveG4 {
  return {
    topGamesStakes: fluctuateArray(prev.topGamesStakes, 0.08),
  };
}

// --- Leaderboard name pool ---------------------------------------------------
const GAME_NAMES = [
  'Chicken Rush', 'Diamond Crown', 'Lucky Drops', 'Royal Coins', 'Mines & Treasures',
  'Golden Sphinx', 'Neon Blaze', 'Dragon Hoard', 'Wild Safari', 'Aztec Gold',
  'Cash Volcano', 'Star Burst', 'Fruit Frenzy', 'Thunder Reels', 'Pirate Plunder',
];

function pickUnique(pool: string[], n: number): string[] {
  const copy = [...pool];
  const result: string[] = [];
  for (let i = 0; i < n && copy.length; i++) {
    result.push(copy.splice(Math.floor(Math.random() * copy.length), 1)[0]);
  }
  return result;
}

// --- Selector: live slice → view shape ---------------------------------------
export function selectSpotlight(live: StatsLiveG4): StatsTopTable {
  const sorted = [...live.topGamesStakes].sort((a, b) => b - a);
  const names = pickUnique(GAME_NAMES, sorted.length);
  return {
    id: 'top-games',
    primaryLabel: 'Top Games',
    secondaryLabel: 'Stakes',
    rows: sorted.map((value, i) => ({ name: names[i], value })),
  };
}
